// frontend/src/lib/riskQuestionSet.js
import {
  AUDIT_QUESTIONS,
  QUESTION_ANSWER_CONTRACT,
  QUESTION_BANK_VERSION,
} from "../data/questions";

const EXPECTED_QUESTION_COUNT = 60;
const ALLOWED_GRAVITIES = new Set([2, 4, 6, 10]);

/**
 * Permanent Detailed Assessment question-bank contract.
 *
 * YES_PASS_V1:
 * - YES = satisfactory / no Remediation finding.
 * - NO = meaningful control deficiency / Remediation finding.
 * - UNSURE = verification required / Remediation finding.
 *
 * The Detailed Assessment fails closed if the bank drifts from the approved
 * 60-question structure, gravity framework, or answer contract.
 */
export function validateQuestionBankContract(bank = AUDIT_QUESTIONS) {
  const issues = [];
  const rows = Array.isArray(bank) ? bank : [];

  if (rows.length !== EXPECTED_QUESTION_COUNT) {
    issues.push(`Expected ${EXPECTED_QUESTION_COUNT} questions; found ${rows.length}.`);
  }

  const seen = new Set();
  rows.forEach((q, index) => {
    const id = Number(q?.id);
    if (!Number.isInteger(id) || id < 1 || id > EXPECTED_QUESTION_COUNT) {
      issues.push(`Question at index ${index} has invalid id: ${String(q?.id)}.`);
    } else if (seen.has(id)) {
      issues.push(`Duplicate question id: ${id}.`);
    } else {
      seen.add(id);
    }

    if (!String(q?.title || "").trim()) issues.push(`Question ${String(q?.id)} is missing title.`);
    if (!String(q?.text || "").trim()) issues.push(`Question ${String(q?.id)} is missing text.`);
    if (!String(q?.guidance || "").trim()) issues.push(`Question ${String(q?.id)} is missing guidance.`);
    if (!String(q?.category || "").trim()) issues.push(`Question ${String(q?.id)} is missing category.`);
    if (!String(q?.playbook_id || "").trim()) issues.push(`Question ${String(q?.id)} is missing playbook_id.`);
    if (q?.answer_contract !== QUESTION_ANSWER_CONTRACT) {
      issues.push(`Question ${String(q?.id)} does not use ${QUESTION_ANSWER_CONTRACT}.`);
    }
    if (!ALLOWED_GRAVITIES.has(Number(q?.gravity))) {
      issues.push(`Question ${String(q?.id)} has unsupported gravity ${String(q?.gravity)}.`);
    }
  });

  for (let id = 1; id <= EXPECTED_QUESTION_COUNT; id += 1) {
    if (!seen.has(id)) issues.push(`Question id ${id} is missing.`);
  }

  return {
    valid: issues.length === 0,
    issue_count: issues.length,
    issues,
    question_count: rows.length,
    question_bank_version: QUESTION_BANK_VERSION,
    answer_contract: QUESTION_ANSWER_CONTRACT,
  };
}

function assertQuestionBankContract() {
  const result = validateQuestionBankContract(AUDIT_QUESTIONS);
  if (!result.valid) {
    throw new Error(
      `Detailed Assessment question bank failed ${QUESTION_ANSWER_CONTRACT}: ${result.issues.join(" | ")}`,
    );
  }
}

export function getQuestionBankVersion() {
  return QUESTION_BANK_VERSION;
}

/**
 * Normalize jurisdictions to stable uppercase tokens.
 * Detailed Assessment always uses the full 60-question bank; scope metadata is
 * retained for context, overlays, exposure modeling, and Remediation guidance.
 */
export function normalizeJurisdictions(config) {
  const raw = config?.jurisdictions ?? config?.jurisdiction ?? [];
  const arr = Array.isArray(raw) ? raw : [raw];

  const normalized = arr
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .map((x) => x.toUpperCase());

  const set = new Set(normalized);
  if (set.has("US_FED") || set.has("US_STATE")) set.add("US");

  return Array.from(set);
}

/**
 * Applicability metadata is advisory only. It never filters the Detailed
 * Assessment. Questions that may be non-applicable are framed so a documented
 * applicability determination can support a defensible YES.
 */
export function questionAppliesToSelection(question, selectedJurisdictions) {
  const qJ = question?.jurisdictions;
  if (!qJ) return true;

  const qArr = Array.isArray(qJ) ? qJ : [qJ];
  const normalized = qArr
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .map((x) => x.toUpperCase());

  if (!normalized.length) return true;
  if (normalized.includes("ALL")) return true;

  const sel = new Set(Array.isArray(selectedJurisdictions) ? selectedJurisdictions : []);
  if (sel.has("US_FED") || sel.has("US_STATE")) sel.add("US");

  if (!sel.size) return true;
  return normalized.some((j) => sel.has(j));
}

/**
 * Build the active question list in stable numeric order.
 * PRODUCT RULE: Always return the full validated master bank.
 */
export function getRelevantQuestions(config) {
  assertQuestionBankContract();

  const selected = normalizeJurisdictions(config);
  const seen = new Set();
  const out = [];

  AUDIT_QUESTIONS.forEach((q) => {
    if (!q || q.id === undefined || q.id === null) return;

    const key = String(q.id);
    if (seen.has(key)) return;
    seen.add(key);

    out.push({
      ...q,
      __applies_to_selected_scope: questionAppliesToSelection(q, selected),
    });
  });

  out.sort((a, b) => Number(a?.id) - Number(b?.id));
  return out;
}
